import React, { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import {
  NodeToolbar,
  NodeResizeControl,
  ResizeControlVariant,
  ViewportPortal,
  Position,
  useStoreApi,
  type NodeProps,
  type Node,
} from '@xyflow/react'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import type { UpdateCanvasNote } from '@/src/renderer/lib/db/models'
import {
  getCanvasNoteColor,
  getCanvasNoteRingColor,
  DEFAULT_CANVAS_NOTE_WIDTH,
  DEFAULT_CANVAS_NOTE_HEIGHT,
  MIN_CANVAS_NOTE_SIZE,
  MAX_CANVAS_NOTE_SIZE,
  computeStickyFontSize,
  getNoteLineHeight,
  getCanvasFontVersion,
  subscribeCanvasFontReady,
} from '../utils/canvasNoteStyle'
import { getCanvasNoteFont } from '../utils/canvasFonts'
import {
  useCanvasPointerBusy,
  useOptionsBarPlacement,
  useSoloSelected,
  useSpawnHovered,
} from '../hooks/useCanvasOptionsBar'
import { useIsPeeling, useIsTapped } from '../hooks/useCanvasTapped'
import { CanvasTape } from '../CanvasTape'
import { canvasTapeIdentity } from '../tapes/canvasTapes'
import { usePresence } from '../hooks/usePresence'
import { useOptionPreview } from '../hooks/useOptionPreview'
import { CanvasOptionsBar } from '../optionsBar'
import type { CanvasOptionsBarApplyState } from '../optionsBar/CanvasOptionsBar.types'
import { CanvasSpawnHandles } from '../CanvasSpawnHandles'
import { RotateHandles } from '../RotateHandles'
import { useRotateDrag } from '../hooks/useRotateDrag'
import { canvasNoteRotations, rotatedBounds } from '../utils/canvasRotation'
import { releaseTextSelection } from '../utils/canvasTextSelection'
import { OPTIONS_BAR_OFFSET } from '../optionsBar/CanvasOptionsBar.types'
import { MemoAuthor } from './MemoAuthor'
import {
  getCanvasNoteShadowUrl,
  CANVAS_NOTE_SHADOW_MARGIN as SHADOW_MARGIN,
} from '../utils/canvasNoteShadow'
import { isConverted, isEditedSinceConversion, isConvertible } from '../utils/noteToTask'
import type { ResizeCorner, ResizeMode } from '../utils/canvasAlignment'
import type { NoteNodeData } from './NoteNode.types'
import './NoteNode.scss'
import { WithTooltip } from '@/src/renderer/components/ui/tooltip'

/**
 * Die Höhe, die der **Autor** dem Text wegnimmt: sein Avatar (24px) plus der
 * Abstand zur Unterkante (8px).
 *
 * Sie geht von der Innenhöhe ab, BEVOR die Schriftgröße gesucht wird — sonst
 * schöbe die letzte Zeile unter den Avatar.
 */
const STICKY_AUTHOR_ROOM = 32

/** Frames, die auf einen messbaren (und damit fokussierbaren) Node gewartet wird. */
const MAX_FOCUS_ATTEMPTS = 10

/**
 * Die vier Ecken, an denen sich die Größe ziehen lässt. Bewusst NUR die Ecken:
 * `NodeResizer` brächte zusätzlich vier Kanten-Linien mit, die hier weder
 * gewollt noch (bei festem Seitenverhältnis) sinnvoll sind.
 */
const RESIZE_CORNERS: readonly ResizeCorner[] = [
  'top-left',
  'top-right',
  'bottom-left',
  'bottom-right',
]

/**
 * A "Notiz" — the compact, adaptive-font-size canvas element (custom
 * React Flow node type `'note'`). Fixed size, no title, no manual resize
 * (that's the Notiz type, a later phase). Selecting it reveals a small
 * options bar (emoji, color, delete) via React Flow's NodeToolbar.
 *
 * @example
 * <ReactFlow nodeTypes={{ note: NoteNode }} ... />
 */
export const NoteNode: React.FC<NodeProps<Node<NoteNodeData>>> = ({
  data,
  selected,
  // Die LAUFENDEN Maße aus React Flow. Sie ändern sich schon während des
  // Ziehens an einer Ecke; `note.width`/`note.height` kämen aus der Datenbank
  // und damit erst beim Loslassen — der Zettel bliebe bis dahin stehen,
  // während die Eckpunkte davonwandern.
  width: nodeWidth,
  height: nodeHeight,
  // Die Lage auf der Fläche — für den Aufsatz der Ziehpunkte, der außerhalb
  // dieses Nodes gezeichnet wird und deshalb Canvas-Koordinaten braucht.
  positionAbsoluteX,
  positionAbsoluteY,
}) => {
  const { t } = useTranslation()
  const dataSource = useDataSource()
  const { note, startEditing = false, editingEnabled = true, onResizeStart, onEdit, onDelete, onOpenTask, onSpawnHover, onSpawn, onApply, editRequest, onEditingChange, onTapeClick, onTape, onRetape, onUntape, onPeeled, onAngle, colorPreview, fontPreview } = data

  /*
    Schreiben geht über `CanvasFlow` — dort liegt der Rückgängig-Stapel.
    Fehlen die Rückrufe (etwa in einer Story oder einem Test, der den Node
    allein rendert), schreibt die Notiz weiterhin selbst: Sie soll ohne den
    Canvas nicht kaputtgehen, nur eben ohne Schritt.
  */
  const edit = useCallback((id: string, before: UpdateCanvasNote, after: UpdateCanvasNote) => {
    if (onEdit) onEdit(id, before, after)
    else void dataSource.updateCanvasNote(id, after)
  }, [onEdit, dataSource])

  const remove = useCallback((id: string) => {
    if (onDelete) onDelete(id)
    else void dataSource.deleteCanvasNote(id)
  }, [onDelete, dataSource])

  const converted = isConverted(note)
  const editedSince = isEditedSinceConversion(note)

  /*
    Ob das **Eselsohr** knicken DARF.

    Beim ersten Zeichnen nicht: Ein schon übernommener Zettel trägt die Ecke
    als Zustand, nicht als Ereignis — ein Canvas, den man öffnet, soll nicht
    mit einem Dutzend gleichzeitig knickender Ecken behaupten, gerade sei
    etwas geschehen.

    Einmal scharf, bleibt es scharf. Genau deshalb klappt das **Rückgängig**
    die Ecke wieder auf und das Alt-Ziehen lässt sie sichtbar auf den
    zurückbleibenden Zettel wandern: dieselbe Transition, beide Richtungen.
  */
  /*
    Beim Rendern verglichen, NICHT in einem Effekt.

    Ein Effekt läuft erst nach dem Zeichnen: Der Knick stünde bereits fertig
    da, und die Transition käme einen Durchlauf zu spät — sie hätte nichts
    mehr zu überblenden. Genau so knallte das Eselsohr beim Übernehmen
    schlagartig heraus, statt hereinzurollen.

    Die Zustandsanpassung während des Renderns lässt React sofort noch einmal
    durchlaufen, bevor irgendetwas am Bildschirm ankommt. Beide Klassen
    erreichen die Hülle damit im SELBEN Schritt — und eine Transition, die
    zusammen mit der Wertänderung scharf wird, läuft: Der Browser liest die
    Übergangsregeln aus dem Zustand NACH der Änderung, den alten Wert kennt er
    noch.
  */
  const [seenConverted, setSeenConverted] = useState(converted)
  const [foldAnimates, setFoldAnimates] = useState(false)
  if (converted !== seenConverted) {
    setSeenConverted(converted)
    setFoldAnimates(true)
  }

  /**
   * Klick auf das Ohr — zur Aufgabe.
   *
   * `stopPropagation` auf dem `pointerdown`: Sonst fasste React Flow den
   * Zettel an und der Klick würde zum Anfang eines Verschiebens; der
   * Doppelklick öffnete zusätzlich den Bearbeiten-Modus.
   */
  const handleEarPointerDown = useCallback((event: React.PointerEvent) => {
    event.stopPropagation()
  }, [])
  /**
   * Und der Doppelklick ebenso: Er steigt sonst zum Zettel auf und öffnete den
   * **Bearbeiten-Modus** — hinter dem Sprung, den der erste Klick schon
   * ausgelöst hat.
   */
  const handleEarDoubleClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation()
  }, [])
  const handleEarClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation()
    if (note.taskId) onOpenTask?.(note.taskId)
  }, [note.taskId, onOpenTask])
  const [content, setContent] = useState(note.content)
  // Bearbeiten-Modus (siehe CONTEXT.md): nicht dasselbe wie ausgewählt.
  const [isEditing, setIsEditing] = useState(startEditing)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Beginn und Ende melden; beim Verschwinden des Nodes gilt „nicht mehr".
  const onEditingChangeRef = useRef(onEditingChange)
  onEditingChangeRef.current = onEditingChange
  useEffect(() => {
    onEditingChangeRef.current?.(note.id, isEditing)
    return () => { if (isEditing) onEditingChangeRef.current?.(note.id, false) }
  }, [isEditing, note.id])

  // `isEditing` als Ref gespiegelt: der Spiegel-Effekt unten darf NICHT an
  // ihm hängen. Sonst liefe er beim Verlassen sofort — während der
  // Dexie-Schreibvorgang noch unterwegs ist — und setzte den Text kurz auf den
  // alten Stand zurück, bis die Live-Query nachkommt.
  const isEditingRef = useRef(isEditing)
  isEditingRef.current = isEditing

  // Sync from the live DB record — but never while the user is mid-edit,
  // or a re-render from our own blur-commit (or another device's write)
  // would yank the cursor / overwrite an in-flight keystroke.
  useEffect(() => {
    if (!isEditingRef.current) setContent(note.content)
  }, [note.content])

  /** Nur festschreiben — ohne den Bearbeiten-Modus anzufassen. */
  const writeContent = useCallback(() => {
    if (content !== note.content) {
      // Ein Schritt je Bearbeiten-Sitzung, nicht je Tastendruck: Der Text
      // wird beim Verlassen festgeschrieben, und „Notiz beschriftet" ist
      // genau die Körnung, die man zurücknehmen will. Innerhalb des Textfelds
      // gehört ⌘Z ohnehin der Eingabe (siehe `useCanvasHistoryShortcut`).
      edit(note.id, { content: note.content }, { content })
    }
  }, [content, note.content, note.id, edit])

  /*
    Der EINE Ausstieg aus dem Bearbeiten-Modus — alle Wege laufen hier durch
    (Blur, Escape, ⌘Enter, Hand-Werkzeug, Abwahl). Und er räumt pro forma
    Textauswahl und Textfokus ab (`releaseTextSelection`), ohne Bedingung:
    Bedingte Fassungen ließen in der App eine Markierung stehen.

    Der Riegel: `releaseTextSelection` nimmt dem Feld den Fokus, das feuert
    `onBlur`, und `onBlur` ist wieder dieser Handler — ohne Riegel schriebe
    er den Text zweimal fest (zwei Undo-Schritte).
  */
  const committingRef = useRef(false)
  const commitContent = useCallback(() => {
    if (committingRef.current) return
    committingRef.current = true
    try {
      setIsEditing(false)
      writeContent()
      releaseTextSelection(textareaRef.current)
    } finally {
      committingRef.current = false
    }
  }, [writeContent])

  // Beim Abbau festschreiben: React ruft `onBlur` dabei nicht auf, wer also
  // beim Tippen das Panel schloss, verlor den Text. Über ein Ref, damit die
  // Aufräumfunktion nicht bei jedem Tastendruck neu gebunden wird.
  //
  // Bewusst `writeContent` statt `commitContent`: StrictMode ruft jeden Effekt
  // doppelt auf — mounten, aufräumen, mounten. Beendete die Aufräumfunktion
  // dabei den Bearbeiten-Modus, ginge die frisch abgesetzte Notiz sofort
  // wieder zu, obwohl Modus und Fokus zuvor korrekt gesetzt waren. Den Modus
  // zu ändern ist beim Abbau ohnehin sinnlos — die Komponente verschwindet ja.
  const writeContentRef = useRef(writeContent)
  writeContentRef.current = writeContent
  // Der Werkzeugwechsel unten will dagegen beides: schreiben UND den Modus
  // beenden — dort verschwindet die Notiz ja nicht, sie wird nur unantastbar.
  const commitContentRef = useRef(commitContent)
  commitContentRef.current = commitContent
  useEffect(() => () => {
    if (isEditingRef.current) writeContentRef.current()
  }, [])

  // `startEditing` als Anfangswert allein genügt NICHT: Der Node mountet,
  // sobald die Live-Query die neue Zeile meldet — das kann vor dem Render
  // passieren, der das Flag trägt. Der Anfangswert ist dann längst gelesen,
  // und die frisch abgesetzte Notiz bliebe stumm (genau so trat es auf).
  //
  // Deshalb auch auf ein später eintreffendes Flag reagieren, aber nur ein
  // einziges Mal pro Node: sonst risse jedes weitere Datenupdate den Text
  // wieder auf, während man längst woanders arbeitet.
  const honouredStartEditing = useRef(startEditing)
  useEffect(() => {
    if (!startEditing || honouredStartEditing.current) return
    honouredStartEditing.current = true
    setIsEditing(true)
  }, [startEditing])

  // Der Fokus folgt dem Modus, der Caret ans Ende des Textes — ein
  // Doppelklick landet sonst mitten in einem Wort.
  //
  // Über mehrere Frames, weil `focus()` auf einem unsichtbaren Element
  // wirkungslos verpufft und der Effekt nicht von selbst noch einmal liefe —
  // `isEditing` ändert sich ja nicht mehr.
  //
  // Der Regelfall greift beim ersten Versuch: die Nodes bringen ihre Maße mit
  // (siehe `noteToFlowNode`), React Flow rendert sie also sofort sichtbar. Die
  // Wiederholung ist die Absicherung für den Fall, dass ein Node doch einmal
  // erst gemessen werden muss. Begrenzt, damit die Schleife endet, statt ewig
  // zu laufen, wenn der Fokus aus einem anderen Grund ausbleibt.
  useEffect(() => {
    if (!isEditing) return

    let frame = 0
    let attemptsLeft = MAX_FOCUS_ATTEMPTS

    const tryFocus = () => {
      const el = textareaRef.current
      if (!el) return
      el.focus()
      if (document.activeElement === el) {
        const end = el.value.length
        el.setSelectionRange(end, end)
        return
      }
      if (--attemptsLeft > 0) frame = requestAnimationFrame(tryFocus)
    }

    tryFocus()
    return () => cancelAnimationFrame(frame)
  }, [isEditing])

  // Nach dem Bearbeiten zurück an den Anfang scrollen. Beim Tippen folgt das
  // Feld des Carets; passt der Text an der 12px-Untergrenze nicht mehr
  // ganz auf den Zettel, bliebe er sonst mitten im Text stehen und die ersten
  // Zeilen wären auf dem ruhenden Zettel unsichtbar.
  //
  // Und die Textauswahl einklappen: Das Feld bleibt ruhend gemountet, eine im
  // Bearbeiten-Modus markierte Stelle bliebe sonst blau stehen, während der
  // Zettel längst abgewählt ist.
  useEffect(() => {
    if (isEditing) return
    const el = textareaRef.current
    if (el) el.scrollTop = 0
  }, [isEditing, content])

  // Das Hand-Werkzeug übernimmt: Modus beenden und festschreiben. Sonst bliebe
  // der Tastaturfokus im Textfeld und man könnte weitertippen, obwohl dort
  // „nur navigieren" gilt.
  useEffect(() => {
    if (editingEnabled || !isEditingRef.current) return
    commitContentRef.current()
  }, [editingEnabled])


  // Für den Doppelklick unten: Er reduziert eine Mehrfachauswahl auf diese
  // Notiz. `addSelectedNodes` ersetzt die Auswahl, solange kein Modifikator
  // gehalten wird (@xyflow/react, index.js:3551).
  const store = useStoreApi()

  /*
    Die Bitte von außen (`editRequest`, siehe Typ): bei jeder Erhöhung öffnen.
    Der Anfangswert zählt nicht — sonst ginge ein Zettel, dessen Node mit
    einer alten Bitte neu aufgebaut wird, ungefragt auf.

    Und AUSWÄHLEN, wie der Doppelklick auf den Zettel selbst: Der Ring liegt
    auch auf einem nur überfahrenen Zettel, und „abgewählt heißt nicht im
    Bearbeiten-Modus" schlösse den Text sonst im selben Atemzug wieder.
  */
  const editRequestRef = useRef(editRequest)
  useEffect(() => {
    if (editRequest === editRequestRef.current) return
    editRequestRef.current = editRequest
    if (!editRequest || !editingEnabled) return
    store.getState().addSelectedNodes([note.id])
    setIsEditing(true)
  }, [editRequest, editingEnabled, store, note.id])

  /*
    **Abgewählt heißt: nicht im Bearbeiten-Modus** (CONTEXT.md). Der Klick
    daneben allein beendet ihn nicht: React Flows Fläche fängt das
    `mousedown` ab (fürs Schieben und Aufziehen), und ohne `mousedown`
    wandert kein Fokus, also kommt kein `blur`. Der Zettel verlor seinen
    Rahmen, das Feld blieb fokussiert und beschreibbar — seine Markierung
    stand blau auf einem Zettel, der abgewählt aussah.

    Deshalb die harte Regel, bei JEDEM Render ohne Auswahl und nicht nur beim
    Übergang: Läuft der Modus, wird festgeschrieben; hat das Feld trotzdem
    noch Fokus oder Auswahl, wird beides abgeräumt. Einzige Ausnahme ist die
    frisch abgesetzte Notiz — sie beginnt unausgewählt im Bearbeiten-Modus
    und war noch nie ausgewählt.
  */
  const everSelectedRef = useRef(Boolean(selected))
  if (selected) everSelectedRef.current = true
  useEffect(() => {
    // Die Wahrheit über die Auswahl steht im Store — die Prop kann einen
    // Render hinterherhinken (Doppelklick wählt aus UND öffnet im selben
    // Tick), und dann schlösse die Regel den Modus, der gerade aufgeht.
    if (selected || store.getState().nodeLookup.get(note.id)?.selected) return
    if (startEditing && !everSelectedRef.current) return
    if (isEditingRef.current) {
      commitContentRef.current()
      return
    }
    const el = textareaRef.current
    if (el && (document.activeElement === el || el.selectionStart !== el.selectionEnd)) {
      releaseTextSelection(el)
    }
  }, [selected, isEditing, startEditing, store, note.id])

  const handleOpenEditor = useCallback((e: React.SyntheticEvent) => {
    if (!editingEnabled) return
    /*
      **Ein geklebter Zettel wird nicht bearbeitet** — eingefroren, und zwar
      ganz (CONTEXT.md, Klebestreifen); der einzige Weg hinaus ist das Ablösen.

      Die Prüfung steht HIER und nicht am Streifen: Auf dem Papier kommt ohnehin
      kein Doppelklick an (der Zeiger fällt hindurch), aber der Streifen holt
      sich seine Ereignisse zurück — er ist der Griff —, und von ihm blubberte
      der Doppelklick nach oben und öffnete das Textfeld (gemeldet 2026-09-06).
      An der Stelle, die das Bearbeiten ÖFFNET, gilt die Regel für jeden Weg
      dorthin; am Griff müsste jeder neue sie erneut mitbringen.

      `note.tape` und nicht `taped`: Das steht weiter unten und läge in der
      Abhängigkeitsliste noch in seiner zeitlichen Totzone.
    */
    if (note.tape) return
    // React Flow hängt seinen Doppelklick-Zoom ans ZoomPane, das die Nodes als
    // Kinder enthält — ohne das hier zoomte der Canvas beim Öffnen mit hinein.
    e.stopPropagation()
    // Die Auswahl schrumpft auf diese Notiz. Ohne das bliebe bei einer
    // Mehrfachauswahl der Gruppenrahmen um alle stehen, während man in einer
    // einzelnen tippt — der Rahmen behauptete eine Gruppe, die gerade keine
    // mehr ist. Führt die Regel „Klick auf eine andere Notiz hebt auf" zu
    // Ende: Ein Doppelklick ist ein Klick mit mehr Absicht, nicht mit weniger.
    store.getState().addSelectedNodes([note.id])
    setIsEditing(true)
  }, [editingEnabled, store, note.id, note.tape])

  /**
   * Die Eingabetaste öffnet den Text der ausgewählten Notiz.
   *
   * Der Listener hängt am `window` und nicht an der Karte: Beim Auswählen
   * bekommt React Flows Node-Hülle den Fokus — das ELTERNelement der Karte.
   * Tastaturereignisse blubbern nach oben, ein Handler an der Karte (oder gar
   * am Textfeld, das ruhend keine Pointer-Events annimmt) bekäme sie nie zu
   * sehen.
   *
   * Nur die ausgewählte Notiz horcht, es ist also höchstens ein Listener
   * gleichzeitig aktiv — außer bei Mehrfachauswahl, die es noch nicht gibt.
   */
  useEffect(() => {
    if (!selected || isEditing || !editingEnabled) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      // Nicht, während anderswo getippt wird — dort schickt Enter eine Zeile ab.
      const tag = (e.target as HTMLElement | null)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return

      e.preventDefault()
      setIsEditing(true)
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selected, isEditing, editingEnabled])

  /**
   * Im Bearbeiten-Modus bleibt blankes Enter der Zeilenumbruch. Hinaus führen
   * Escape und ⌘/⌃+Enter — beide über `blur`, damit das Festschreiben durch
   * denselben Pfad läuft, statt ihn zu doppeln.
   *
   * **Und die Notiz bleibt danach ausgewählt.** Der Bearbeiten-Modus ist NICHT
   * dasselbe wie ausgewählt (CONTEXT.md): Wer den Text abschließt, hat die
   * Notiz weiterhin in der Hand — sie soll sich verschieben, färben und mit
   * Enter wieder öffnen lassen, ohne dass man sie erst wieder anklickt. Ohne
   * das endete der Tastaturfluss beim ersten Escape.
   *
   * Dieselbe Regel gilt schon beim Duplizieren mit ⌘D (`CanvasFlow`), wo die
   * Auswahl eigens auf die neue Notiz übergeht.
   *
   * Bewusst HIER und nicht in `commitContent`: Das läuft bei JEDEM `blur` —
   * auch, wenn man auf eine andere Notiz klickt. Dort die Auswahl an sich zu
   * ziehen, nähme sie der gerade angeklickten wieder weg.
   *
   * **Und der Tastaturfokus wandert auf den Node.** `blur()` allein ließe ihn
   * auf `body` fallen — und Entf käme nicht mehr am Canvas an (das Kürzel
   * horcht dort, nicht am Dokument): Der eben beschriebene Zettel ließe sich
   * nicht löschen, ohne ihn erst anzuklicken. Derselbe Weg wie `returnFocus`
   * nach einer Aktion in der Leiste.
   */
  const handleContentKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const isFinish = e.key === 'Enter' && (e.metaKey || e.ctrlKey)
    if (e.key !== 'Escape' && !isFinish) return
    e.preventDefault()
    e.stopPropagation()
    e.currentTarget.blur()
    // Wie beim Doppelklick oben: die Auswahl schrumpft auf diese eine Notiz.
    store.getState().addSelectedNodes([note.id])
    rootRef.current?.closest<HTMLElement>('.react-flow__node')?.focus({ preventScroll: true })
  }, [store, note.id])

  const handleDelete = useCallback(() => {
    remove(note.id)
  }, [note.id, remove])

  const applyState: CanvasOptionsBarApplyState = isConverted(note) ? 'done' : isConvertible(note) ? 'ready' : 'empty'
  const handleApply = useCallback(() => {
    if (isEditingRef.current) commitContentRef.current()
    onApply?.(note.id)
  }, [onApply, note.id])

  // Das Ziehen an einer Ecke wird nach oben gemeldet, statt hier abgewickelt
  // zu werden: Ausrichten, Rastern und Festschreiben liegen in `CanvasFlow`,
  // wo die Nachbarn bekannt sind. Von hier kommt nur die eine Angabe, die dort
  // fehlt — welche Ecke der Zeiger führt.
  //
  // Insbesondere wird React Flows `snapToGrid` NICHT mehr eingeschaltet: es
  // rundete die Maße schon vor allen Handlern, und die Ausrichtung an einem
  // Nachbarn könnte das Raster dann nie schlagen.
  const handleResizeStart = useCallback((corner: ResizeCorner, mode: ResizeMode = 'corner') => {
    onResizeStart?.(note.id, corner, mode)
  }, [onResizeStart, note.id])

  // `||` statt `??`: ein noch ungemessener Node meldet 0, nicht `undefined` —
  // mit `??` fiele die Notiz dann auf Breite 0 zusammen.
  const width = nodeWidth || note.width || DEFAULT_CANVAS_NOTE_WIDTH
  const height = nodeHeight || note.height || DEFAULT_CANVAS_NOTE_HEIGHT

  /*
    Die **Neigung** und ihr Preis.

    `?? 0`: Eine Zeile aus einer älteren Fassung kennt das Feld nicht — und die
    Zahl ginge als `NaN` in `rotate: NaNdeg`, wo sie den Zettel aus dem Bild
    kippte. (Die Dexie-Migration v31 trägt sie nach; das hier ist das Netz für
    Stories, Tests und die Frames davor.)

    **Der Überstand** ist, wie weit die obere Ecke des geneigten Papiers über
    das Rechteck hinausragt. Die **Options-Leiste** braucht ihn zweimal: für
    ihren Abstand und für die Entscheidung, ob sie unter den Zettel klappt. Ein
    240px-Memo bei 42° hebt seine Ecke um 49px — mehr als die 40 des
    `OPTIONS_BAR_OFFSET`, die Ecke stäche sonst durch die Leiste.

    Gerechnet aus dem GESPEICHERTEN Winkel, nicht aus der Vorschau: Während
    eines Zugs ist die Leiste ohnehin weg (`pointerBusy`), und ein Abstand, der
    in jedem Frame neu fiele, zöge ein Rendern der Leiste nach sich.
  */
  const rotation = note.rotation ?? 0
  const rotationLift = (rotatedBounds(width, height, rotation).height - height) / 2
  /*
    Die Vorschau des Farbwählers färbt den Zettel, nicht seine Leiste und
    nicht den Verlaufsschritt — die lesen weiter `note.color`.
  */
  const [ownPreview, setOwnPreview] = useOptionPreview(note.color)
  const shownColor = (ownPreview ?? colorPreview)?.color ?? note.color
  const color = getCanvasNoteColor(shownColor)
  const hex = color.hex
  // Einmal je Notiz: `getCanvasNoteShadowUrl` reicht eine von drei geteilten
  // Texturen durch, die Auswahl hängt allein an der Kennung.
  const shadowUrl = useMemo(() => getCanvasNoteShadowUrl(note.id), [note.id])

  /*
    Die Schriftgröße hängt an der GEMESSENEN Textbreite — und damit an der
    Schrift, die der Browser gerade tatsächlich zeichnet.

    „Open Sans" kommt per `font-display: swap` nach (globals.scss). Bis dahin
    misst der Canvas dieselbe Ersatzschrift, die auch gezeichnet wird; die
    Rechnung stimmt. Beim WECHSEL ändert sich die Grundlage, und ohne dieses
    Abonnement bliebe die einmal gewählte Größe stehen — für „Here" auf dem
    Vorgabe-Zettel 62px statt 59px, was in Open Sans 142,7px misst gegen 136px
    Innenbreite: Der Text bräche mitten im Wort um und liefe unten hinaus.

    `useSyncExternalStore` und kein Effekt mit State: Die Fassung ist ein Wert
    außerhalb von React, und genau dafür ist der Hook da.
  */
  const fontVersion = useSyncExternalStore(subscribeCanvasFontReady, getCanvasFontVersion, getCanvasFontVersion)
  // Die Vorschau des Schriftmenüs — samt Zeilenhöhe und errechneter Größe, wie der Klick sie ergäbe.
  const [ownFontPreview, setOwnFontPreview] = useOptionPreview<{ font: string | null }>(note.font)
  const fontShown = ownFontPreview ?? fontPreview
  const shownFont = fontShown ? fontShown.font : note.font
  const font = getCanvasNoteFont(shownFont)
  const lineHeight = getNoteLineHeight(shownFont)
  /*
    Der **Autor** nimmt dem Text Höhe weg — sonst schöbe er die letzte Zeile
    unter die Kante. Nur der Avatar (24px) plus sein Abstand: Am Notizen steht
    kein Name, dafür ist er zu klein (siehe `MemoAuthor` und CONTEXT.md).
  */
  const authorRoom = note.showAuthor ? STICKY_AUTHOR_ROOM : 0
  /*
    Gecacht, und die Font-Version gehört in die Abhängigkeiten: React Flow
    reicht beim Ziehen in jedem Frame eine neue Lage durch, die Notiz rendert
    also je Frame — die absteigende Suche von der Maximalgröße lief dabei
    jedes Mal mit, obwohl sich an Text und Maßen nichts geändert hatte.
  */
  const textHeight = height - authorRoom
  const fontSize = useMemo(
    () => computeStickyFontSize(content, width, textHeight, { fontKey: shownFont }),
    // `fontVersion` steht nicht im Rumpf, sondern nur hier: Sie invalidiert die
    // Rechnung, sobald die echte Schrift geladen ist (siehe oben).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [content, width, textHeight, shownFont, fontVersion],
  )

  /*
    Alle drei rechnen nur für den ausgewählten Zettel: Ihre Selektoren laufen
    bei jeder Store-Änderung, und React Flow schreibt beim Ziehen in jedem
    Frame. Ein nicht ausgewählter Zettel zeigt ohnehin keine Leiste.
  */
  /*
    Der FRISCHE Zettel: Er startet im Bearbeiten-Modus, ohne ausgewählt zu sein,
    und zeigt als einzige Ausnahme die Leiste trotzdem — sofort, nicht erst
    nach der Wartezeit einer Geste (CONTEXT.md, Bearbeiten-Modus). Sobald
    dieser erste Bearbeiten-Modus endet, ist er ausgewählt (siehe Escape
    unten), und die gewöhnliche Regel gilt.
  */
  const fresh = startEditing && isEditing && !selected
  const soloSelected = useSoloSelected(selected)
  const pointerBusy = useCanvasPointerBusy(selected)
  /*
    Der **Klebestreifen**: Ein geklebter Zettel wird nicht ausgewählt, sondern
    **angetippt** (siehe `useCanvasTapped`). Angetippt trägt genau zwei
    Wirkungen — die Leiste und die **Anlegepunkte**. Alles andere, was sonst an
    `selected` hängt (Rahmen, Ziehpunkte, **Tauschpunkt**), bleibt weg: Was man
    nicht tun kann, sieht man auch nicht.
  */
  const taped = Boolean(note.tape)
  const tapped = useIsTapped(note.id)
  const peeling = useIsPeeling(note.id)
  const placement = useOptionsBarPlacement(note.y - rotationLift, selected || fresh || tapped)
  /*
    Steht die Vorschau eines **Anlegepunkts**, weicht die Leiste — am oberen
    und linken Punkt rückt der Zettel dabei selbst, und eine Leiste, die
    mitwanderte, führe quer über die gerückte Reihe. Der Merker hängt an den
    Punkten (siehe `useSpawnHovered`) und liegt im Node, nicht in `data`: Der
    Abgleich in `CanvasFlow` vergleicht nur `note`, `startEditing` und
    `editingEnabled` — ein neues Datenfeld bliebe in alten Nodes stehen.
  */
  /*
    **Ein geklebter Zettel trägt keine Anlegepunkte** (CONTEXT.md,
    Klebestreifen). Er ist aus dem Spiel — auch als Ausgangspunkt: Was er noch
    kann, ist gelesen und abgelöst zu werden.
  */
  const spawnHandlesShown = Boolean(
    !taped && selected && soloSelected && editingEnabled && !isEditing && onSpawn,
  )
  const [spawnHovered, setSpawnHovered] = useSpawnHovered(spawnHandlesShown)
  const showBar = taped
    ? tapped
    : fresh || (selected && soloSelected && !pointerBusy && !spawnHovered)
  // Sanft raus wie rein: kurz stehen lassen und abblenden (siehe `usePresence`).
  const barPresence = usePresence(showBar)

  /*
    Der Fokus zurück nach einer Aktion in der Leiste — sonst kämen die Kürzel
    am Canvas (Entf …) nicht mehr an. Beim Schreiben ins Textfeld; die
    Caret bleibt, wo sie war (der Browser behält die Auswahl einer
    Textarea über einen Fokuswechsel hinweg). Sonst auf React Flows
    Node-Wrapper, der bei `nodesFocusable` einen `tabIndex` trägt.
  */
  const rootRef = useRef<HTMLDivElement>(null)
  const returnFocus = useCallback(() => {
    const target = isEditing
      ? textareaRef.current
      : rootRef.current?.closest<HTMLElement>('.react-flow__node')
    target?.focus({ preventScroll: true })
  }, [isEditing])

  /**
   * Ob die Ziehpunkte gelten — Trefferfläche wie Aussehen.
   *
   * Eine Bedingung für beide: Sie werden getrennt gerendert (siehe unten), und
   * liefen sie auseinander, schwebten Punkte über einer Notiz, die man gerade
   * beschreibt, oder man griffe ins Leere.
   *
   * Nur wenn die Notiz als OBJEKT angefasst wird: nicht im Hand-Werkzeug (dort
   * ist an den Objekten nichts zu holen) und nicht im Bearbeiten-Modus (dort
   * ist der Zettel ein Textfeld, siehe CONTEXT.md).
   */
  const showHandles = Boolean(selected) && editingEnabled && !isEditing

  /*
    ── **Neigung** ──────────────────────────────────────────────────────────

    Gedreht wird an vier Zonen schräg außerhalb der Ecken, um die **Mitte** des
    Zettels. Die Geste selbst steht in `useRotateDrag` — sie ist dieselbe wie
    am **Klebestreifen**.

    Geschrieben wird über `edit` und nicht über einen eigenen Rückruf: Es legt
    denselben **Schritt** auf den Stapel und schreibt dieselbe Zeile. Ein
    `onRotate` daneben wäre ein zweiter Weg zur selben Sache — und einer, den
    eine Story oder ein Test ohne Canvas nicht hätte.

    Die Mitte kommt aus dem Umgebungsrechteck der KARTE. Bei einem gedrehten
    Element ist dessen Mittelpunkt weiterhin der wahre Mittelpunkt — gedreht
    wird ja um ihn.
  */
  const rotate = useRotateDrag({
    getCenter: () => {
      const box = rootRef.current?.getBoundingClientRect()
      return box ? { x: box.left + box.width / 2, y: box.top + box.height / 2 } : null
    },
    getRasten: canvasNoteRotations,
    getStart: () => rotation,
    onCommit: angle => edit(note.id, { rotation }, { rotation: angle }),
    // **Doppelklick legt ihn wieder gerade** — als Schritt, ⌘Z holt den Winkel
    // zurück. Steht er schon gerade, ist nichts zu tun; ein leerer Schritt auf
    // dem Stapel wäre ein ⌘Z, das nichts täte.
    onReset: () => { if (rotation !== 0) edit(note.id, { rotation }, { rotation: 0 }) },
    enabled: showHandles,
  })

  /*
    **Die Ziehpunkte weichen, solange gedreht wird.**

    Sonst stünden vier Marken auf dem ungedrehten Rechteck still, während das
    Papier darunter läuft — genau in dem Moment, in dem man auf die Bewegung
    schaut. BEIDE Hälften hängen an derselben Zahl (die unsichtbare
    Trefferfläche wie das gezeichnete Pünktchen): Liefen sie auseinander, bliebe
    eine Trefferfläche über dem laufenden Zettel liegen.
  */
  const showResize = showHandles && !rotate.rotating

  /*
    **Nur einzeln.** Dieselbe Bedingung wie bei **Anlegepunkten** und Leiste,
    nicht die der Ziehpunkte: Ab zwei ausgewählten Elementen gehört die Fläche
    dem **Auswahlrahmen**, und der dreht nicht. Ohne `soloSelected` hinge an
    jedem Mitglied einer Gruppe ein Satz unsichtbarer Zonen über den Nachbarn.
  */
  const showRotate = showHandles && soloSelected

  /*
    Was GEZEICHNET wird: die Vorschau, solange eine läuft — sonst die Zeile.
    Die Vorschau überlebt das Loslassen und fällt erst, wenn die Zeile spricht
    (siehe `useRotateDrag`).
  */
  const shownRotation = rotate.preview ?? rotation


  return (
    <>
      {/*
        Die vier **Dreh-Zonen** — schräg außerhalb der Ecken, unsichtbar, der
        Cursor ist die Ansage. Sie bleiben WÄHREND des Zugs stehen: Der Zeiger
        ist in ihnen aufgenommen, und wer sie mitten in der Geste entfernte,
        risse sie ab.
      */}
      <RotateHandles shown={showRotate} handlers={rotate.handlers} />

      {/* Die TREFFERFLÄCHE der vier Ziehpunkte. Unsichtbar — gezeichnet wird
          im `ViewportPortal` darunter, die Begründung steht dort. */}
      {showResize && RESIZE_CORNERS.map(position => (
        <NodeResizeControl
          key={position}
          position={position}
          keepAspectRatio
          minWidth={MIN_CANVAS_NOTE_SIZE}
          minHeight={MIN_CANVAS_NOTE_SIZE}
          maxWidth={MAX_CANVAS_NOTE_SIZE}
          maxHeight={MAX_CANVAS_NOTE_SIZE}
          className="note-node__handle"
          onResizeStart={() => handleResizeStart(position)}
        />
      ))}

      {/*
        Die beiden senkrechten KANTEN — die zweite Breite.

        Ein **Notiz** ist entweder ein Quadrat oder ein Rechteck, wie in Miro
        und FigJam; dazwischen gibt es nichts. Der Zug an einer Kante ist
        deshalb ein Schalter, kein Regler: Die Breite springt auf einen der
        beiden Werte, sobald der Zeiger die Mitte überschreitet (die Rechnung
        steht in `stickyWidths`/`nextStickyWidth`, gewählt wird sie in
        `CanvasFlow` über den Modus `'edge'`).

        `variant="line"` wie bei der **Notiz**: Die ganze Kante ist Ziehfläche,
        ein Punkt auf halber Höhe wanderte beim Skalieren ständig.

        **Sie treten hinter die Ziehpunkte zurück** — die SCSS kürzt sie oben
        und unten um deren Größe ein. Eine Kante über die volle Höhe läge an
        ihren Enden über den Ecken, und dort will man skalieren, nicht
        verbreitern.

        Ohne `minWidth`/`maxWidth`: Die erlaubten Breiten stehen ohnehin fest,
        und React Flows Grenzen sind hier die falsche Stelle dafür — beim
        größten Zettel liegt das Rechteck bewusst über `MAX_CANVAS_NOTE_SIZE`
        (die Grenze gilt für die SEITE, siehe `stickyWidths`).
      */}
      {showResize && (['left', 'right'] as const).map(side => (
        <NodeResizeControl
          key={side}
          position={side}
          variant={ResizeControlVariant.Line}
          className="note-node__edge"
          onResizeStart={() => handleResizeStart(side === 'left' ? 'top-left' : 'top-right', 'edge')}
        />
      ))}

      {/*
        Das SICHTBARE Pünktchen — außerhalb dieses Nodes gezeichnet.

        **Bedienelemente liegen über der Rückmeldung** (CONTEXT.md). Von hier
        aus ist das nicht zu haben: `.react-flow__node` trägt einen
        Inline-`z-index` und ist damit ein eigener Stapelkontext — nichts
        darin kommt über die 2000 der **Hilfslinie**, auch mit `z-index: 9999`
        nicht. Die Linie schnitt den Punkt dadurch mitten durch.

        Also sind Trefferfläche und Aussehen getrennt: Der
        `NodeResizeControl` oben bleibt, wo React Flow ihn braucht, und
        behält den ganzen Zug samt Seitenverhältnis und Mindestmaß — er ist
        nur unsichtbar. Gezeichnet wird hier, im `ViewportPortal`, wo schon
        Hilfslinie, Maßklammer und Auswahlrahmen liegen.

        Es gibt am Ende EINEN sichtbaren Punkt, nicht zwei. Dieselbe Trennung
        wie beim **Drag Handle** (ADR 0010): Wer über allem liegen muss, darf
        in keinem Kasten stecken.
      */}
      {showResize && (
        <ViewportPortal>
          {RESIZE_CORNERS.map(position => (
            <div
              key={position}
              className="canvas-note-handle"
              style={{
                left: position.endsWith('left')
                  ? positionAbsoluteX
                  : positionAbsoluteX + width,
                top: position.startsWith('top')
                  ? positionAbsoluteY
                  : positionAbsoluteY + height,
                // Dieselbe Zahl wie am Zettel selbst (`--canvas-scale`), sonst
                // liefen sichtbarer Punkt und Trefferfläche beim Zoomen
                // auseinander.
                scale: 'var(--canvas-scale, 1)',
              }}
            />
          ))}
          {/*
            Die vier **Anlegepunkte** — nur am EINZELN ausgewählten Zettel, wie
            die Leiste. Ansicht und Treffer in einem, siehe `CanvasSpawnHandles`.
          */}
          {spawnHandlesShown && (
            <CanvasSpawnHandles
              rect={{ x: positionAbsoluteX, y: positionAbsoluteY, width, height }}
              suspended={pointerBusy}
              onHover={side => {
                setSpawnHovered(side !== null)
                onSpawnHover?.(note.id, side)
              }}
              onSpawn={side => {
                // Der Klick beendet die Geste — auch wenn dieser Zettel
                // ausgewählt bleibt, weil `handleSpawn` früh zurückkehrt
                // (kein Platz, laufende Geste, offenes Anlegen).
                setSpawnHovered(false)
                onSpawn?.(note.id, side)
              }}
            />
          )}
        </ViewportPortal>
      )}

      {/*
        Die **Options-Leiste** — ohne Titel-Umschalter: Eine Notiz hat keinen
        Titel, sein Text IST die Sache.

        Ist der Zettel **geklebt**, bleibt genau ein Knopf übrig: **Ablösen**,
        mit Wort neben dem Zeichen. Alles andere ist an ihm gesperrt, und ein
        toter Knopf, den man sich erklären müsste, ist keiner.
      */}
      <NodeToolbar
        isVisible={barPresence.mounted}
        position={placement === 'below' ? Position.Bottom : Position.Top}
        offset={OPTIONS_BAR_OFFSET + rotationLift}
      >
        <CanvasOptionsBar
          leaving={barPresence.leaving}
          onActionDone={returnFocus}
          buttons={taped ? ['untape', 'retape'] : ['emoji', 'color', 'font', 'author', 'apply', 'tape', 'delete']}
          colorKeys={[note.color]}
          fontKeys={[note.font]}
          authorOn={note.showAuthor}
          placement={placement}
          onColorChange={key => edit(note.id, { color: note.color }, { color: key })}
          onColorPreview={setOwnPreview}
          onFontChange={key => edit(note.id, { font: note.font }, { font: key })}
          onFontPreview={setOwnFontPreview}
          onToggleAuthor={() => edit(note.id, { showAuthor: note.showAuthor }, { showAuthor: !note.showAuthor })}
          onDelete={handleDelete}
          onTape={onTape ? () => onTape(note.id) : undefined}
          onRetape={onRetape ? () => onRetape(note.id) : undefined}
          onUntape={onUntape ? () => onUntape(note.id) : undefined}
          // Übernehmen beendet zuerst den Bearbeiten-Modus (CONTEXT.md): Der
          // Text wird festgeschrieben, dann entsteht die Verbindung.
          onApply={onApply ? handleApply : undefined}
          applyState={applyState}
        />
      </NodeToolbar>

      <div
        ref={rootRef}
        className={
          'note-node'
          + (isEditing ? ' note-node--editing' : '')
          + (converted ? ' note-node--converted' : '')
          + (foldAnimates ? ' note-node--fold-animates' : '')
          + (taped ? ' note-node--taped' : '')
        }
        /*
          Die Fläche als Custom Property, nicht als fertige Regel: die Farbe
          ist Nutzerinhalt und muss von hier kommen, das Rezept (Verlauf,
          Radius, Schatten) bleibt aber in der SCSS.
        */
        /*
          Die Ränder der Schatten-Textur kommen aus dem Generator, nicht aus
          fest getippten Zahlen in der SCSS: liefen sie auseinander, würde die
          Textur gestaucht und ihr äußerer Rand abgeschnitten.
        */
        style={{
          width,
          height,
          // Die Maße AUCH als Custom Properties: Der Schatten muss am
          // **Eselsohr** mitgeschnitten werden, und sein Pseudo-Element ist
          // größer als die Notiz — seine Prozente meinen also etwas anderes.
          // Mit den Maßen als Länge wird die Rechnung dort schlichte Arithmetik
          // (siehe `&::after` in der SCSS).
          '--note-w': `${width}px`,
          '--note-h': `${height}px`,
          /*
            Die **Neigung**. Als Custom Property und nicht als fertiges
            `rotate`: Der Wert ist Nutzerinhalt, das Rezept (Ursprung,
            Übergang, Stapelkontext) bleibt in der SCSS.

            **Im Bearbeiten-Modus richtet sich der Zettel auf.** Dort IST er
            ein Textfeld (CONTEXT.md), und ein Textfeld liegt gerade — bei 42°
            wären Lesen, Caret und Textauswahl sonst eine Zumutung. Die
            Zeile ändert sich dabei nicht; es ist eine Regel der Darstellung,
            und beim Verlassen kippt er zurück.

            Hier und nicht in der SCSS: Eine Inline-Variable schlägt jede
            Regel, ein `.note-node--editing { --note-angle: 0deg }` käme also
            nie zum Zug.
          */
          '--note-angle': `${isEditing ? 0 : shownRotation}deg`,
          '--note-fill': hex,
          // Der satte Ton DIESES Zettels — derselbe, den sein **Tauschpunkt**
          // trägt. Aus der Papierfarbe gerechnet wäre er grau: Die
          // Papierfarben haben kaum Sättigung, und was man ihnen an Helligkeit
          // nimmt, wird stumpf statt farbig. Gebraucht für den **Umriss** beim
          // **Platz tauschen** (siehe SCSS).
          '--note-edge': getCanvasNoteRingColor(shownColor),
          // Nur Weiß und Grau bringen den Knick mit; sonst wird er aus der
          // Fläche gerechnet (siehe `CANVAS_NOTE_COLORS`). Der Hover-Ton geht
          // als EIGENE Variable mit — die SCSS greift ihn in ihrer
          // Hover-Regel ab, weil eine Inline-Variable jede Regel schlüge.
          // `in` und nicht `color.fold`: Die Tafel ist `as const`, ihr Typ
          // also eine Union aus neun Literalen — die sieben bunten kennen die
          // Eigenschaft gar nicht.
          ...('fold' in color ? { '--note-fold-tint': color.fold } : {}),
          ...('foldHover' in color ? { '--note-fold-tint-hover': color.foldHover } : {}),
          // Die gewählte Schrift samt ihrer gemessenen Zeilenhöhe. Beide gehen
          // AUCH in `computeStickyFontSize` ein — liefen Rechnung und
          // Darstellung auseinander, liefe der Text über.
          '--note-font': font.stack,
          '--note-font-weight': font.weight,
          '--note-line-height': lineHeight,
          '--note-shadow': `url(${shadowUrl})`,
          '--note-shadow-l': SHADOW_MARGIN.l,
          '--note-shadow-r': SHADOW_MARGIN.r,
          '--note-shadow-t': SHADOW_MARGIN.t,
          '--note-shadow-b': SHADOW_MARGIN.b,
        } as React.CSSProperties}
        onDoubleClick={handleOpenEditor}
      >
        {note.icon && <span className="note-node__icon">{note.icon}</span>}

        {/*
          Das **Eselsohr**: die umgeknickte Ecke ist Sache der SCSS (ein
          `clip-path` plus ein Verlauf am `::before`), hier steht nur ihr
          Ziel — der Weg zurück zur Aufgabe.

          Ein `<button>` und kein `div`: Er ist über die Tastatur erreichbar
          und trägt seinen Namen selbst. `nodrag` hält React Flow davon ab,
          aus dem Druck ein Verschieben zu machen.
        */}
        {/*
          Der **Klebestreifen** — der Griff des geklebten Zettels. Innerhalb des
          Zettels, damit ihn dessen Tiefe trägt; über das Papier hinausragen
          darf er trotzdem, die Node-Hülle schneidet nicht ab.
        */}
        {taped && onTapeClick && (
          <CanvasTape key={canvasTapeIdentity(note.tape)} noteId={note.id} tape={note.tape!} tapped={tapped} peeling={peeling} onClick={onTapeClick} onPeeled={onPeeled} onAngle={onAngle} disabled={!editingEnabled} />
        )}

        {converted && <span className="note-node__fold" aria-hidden="true" />}
        {converted && (
          <WithTooltip content={t('projectCanvas.note.openTask')}>
            <button
              type="button"
              className="note-node__ear nodrag"
              aria-label={t('projectCanvas.note.openTask')}
              onPointerDown={handleEarPointerDown}
              onDoubleClick={handleEarDoubleClick}
              onClick={handleEarClick}
            />
          </WithTooltip>
        )}

        {/*
          „bearbeitet" — der Text steht nicht mehr so da wie bei der Übernahme,
          und die Aufgabe trägt noch den alten Wortlaut. Es fließt ja nichts
          (CONTEXT.md, „Verbindung"), also ist das die einzige Auskunft, die
          es darüber geben kann.

          Sichtbar wird es erst bei Auswahl oder Überfahren (SCSS) — es steht
          im Nutzertext, und dauerhaft säße es darauf.
        */}
        {editedSince && (
          <WithTooltip content={t('projectCanvas.note.editedHint')}>
            <span className="note-node__edited">
              {t('projectCanvas.note.edited')}
            </span>
          </WithTooltip>
        )}
        {/*
          Der **Autor** — am Notizen nur der Avatar, unten links. Die volle
          Pille der **Notiz** wäre auf einem 72px-Zettel breiter als er selbst;
          der Name steht deshalb im Tooltip. Das **Eselsohr** sitzt oben
          rechts, die beiden kommen sich nicht ins Gehege.
        */}
        {note.showAuthor && (
          <span className="note-node__author">
            <MemoAuthor variant="avatar" chipColor={hex} textColor="inherit" />
          </span>
        )}
        {/*
          Ruhend ohne `nodrag`: der Text reicht den Zeiger an React Flow
          weiter, die Notiz lässt sich also auch am Text anfassen und
          verschieben. `readOnly` allein genügte dafür nicht — ein
          schreibgeschütztes Feld nimmt weiterhin Fokus und Klicks an, deshalb
          zusätzlich `pointer-events: none` in der SCSS.
        */}
        {/*
          Die Schriftgröße hängt an der GEMESSENEN Textbreite. „Open Sans"
          wird per `font-display: swap` nachgeladen; bis dahin misst und
          zeichnet der Browser eine Ersatzschrift — beides stimmt überein.
          Beim Wechsel ändert sich die Grundlage, und ohne dieses Abonnement
          bliebe die einmal gewählte Größe stehen: zu groß für Open Sans, der
          Text bräche mitten im Wort um und liefe unten aus dem Zettel.
        */}
        <textarea
          ref={textareaRef}
          className={`note-node__content${isEditing ? ' nodrag' : ''}`}
          value={content}
          readOnly={!isEditing}
          // Ruhend aus dem Tab-Lauf genommen. `pointer-events: none` (siehe
          // oben) hält die MAUS ab, die Tastatur nicht: Ein
          // schreibgeschütztes Feld bleibt fokussierbar, jeder Notiz hielte
          // sonst zwei Tab-Stopps statt einem — und die **Tab-Reihenfolge**
          // (`useCanvasTabShortcut`) könnte in einem ruhenden Feld parken, wo
          // sie eine Notiz anlegt, obwohl niemand im Bearbeiten-Modus ist.
          tabIndex={isEditing ? undefined : -1}
          style={{ fontSize: fontSize }}
          onChange={e => setContent(e.target.value)}
          onKeyDown={handleContentKeyDown}
          onBlur={commitContent}
        />
      </div>
    </>
  )
}
